import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, Mail, Eye, EyeOff, Shield, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';

const AdminLogin = ({ language, setLanguage }) => {
  const { signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const content = {
    mr: {
      title: 'प्रशासक प्रवेश',
      subtitle: 'कृपया आपली ओळखपत्रे प्रविष्ट करा',
      email: 'ईमेल',
      password: 'पासवर्ड',
      login: 'प्रवेश करा',
      logging: 'प्रवेश करत आहे...',
      invalidCredentials: 'अवैध ईमेल किंवा पासवर्ड',
      loginFailed: 'प्रवेश अयशस्वी',
      networkError: 'नेटवर्क त्रुटी. कृपया पुन्हा प्रयत्न करा',
      emptyFields: 'कृपया सर्व फील्ड भरा',
      footer: 'मिठमुंबरी ग्रामपंचायत प्रशासन प्रणाली'
    },
    en: {
      title: 'Admin Login',
      subtitle: 'Please enter your credentials',
      email: 'Email',
      password: 'Password',
      login: 'Login',
      logging: 'Logging in...',
      invalidCredentials: 'Invalid email or password',
      loginFailed: 'Login failed',
      networkError: 'Network error. Please try again',
      emptyFields: 'Please fill in all fields',
      footer: 'Mithmumbari Gram Panchayat Administration System'
    }
  };

  const currentContent = content[language];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate inputs
    if (!email || !password) {
      setError(currentContent.emptyFields);
      return;
    }

    setLoading(true);

    try {
      const result = await signIn(email, password);
      
      // Check if login failed
      if (result && result.error) {
        setError(currentContent.invalidCredentials);
      } else if (!result || !result.data) {
        setError(currentContent.loginFailed);
      }
    } catch (error) {
      console.error('Login error:', error);
      
      // Provide user-friendly error messages
      if (error.message) {
        if (error.message.toLowerCase().includes('network')) {
          setError(currentContent.networkError);
        } else if (error.message.toLowerCase().includes('invalid') || 
                   error.message.toLowerCase().includes('wrong') ||
                   error.message.toLowerCase().includes('incorrect')) {
          setError(currentContent.invalidCredentials);
        } else {
          setError(error.message);
        }
      } else {
        setError(currentContent.loginFailed);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-teal-50 to-blue-50 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          className="absolute -top-40 -left-40 w-80 h-80 bg-green-200/30 rounded-full blur-3xl"
          animate={{ 
            scale: [1, 1.2, 1],
            x: [0, 50, 0],
            y: [0, 30, 0]
          }}
          transition={{ duration: 20, repeat: Infinity }}
        />
        <motion.div
          className="absolute -bottom-40 -right-40 w-96 h-96 bg-teal-200/30 rounded-full blur-3xl"
          animate={{ 
            scale: [1, 1.3, 1],
            x: [0, -50, 0],
            y: [0, -30, 0]
          }}
          transition={{ duration: 25, repeat: Infinity }}
        />
      </div>

      {/* Language toggle */}
      <div className="absolute top-6 right-6 z-20">
        <div className="bg-white/80 backdrop-blur-sm rounded-full p-1 shadow-lg flex gap-1">
          <button
            onClick={() => setLanguage('mr')}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
              language === 'mr' 
                ? 'bg-gradient-to-r from-green-600 to-teal-600 text-white' 
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            मराठी
          </button>
          <button
            onClick={() => setLanguage('en')}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
              language === 'en' 
                ? 'bg-gradient-to-r from-green-600 to-teal-600 text-white' 
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            English
          </button>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="relative z-10 w-full max-w-md"
      >
        {/* Logo and title */}
        <div className="text-center mb-8">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", duration: 0.8 }}
            className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-green-600 to-teal-600 rounded-2xl shadow-2xl mb-4 transform rotate-3 hover:rotate-0 transition-transform"
          >
            <Shield className="w-10 h-10 text-white" />
          </motion.div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            {currentContent.title}
          </h1>
          <p className="text-gray-600">
            {currentContent.subtitle}
          </p>
        </div>

        {/* Login form */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          className="bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl p-8 border border-white/50"
        >
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Error Message */}
            <AnimatePresence>
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10, height: 0 }}
                  animate={{ opacity: 1, y: 0, height: 'auto' }}
                  exit={{ opacity: 0, y: -10, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="bg-red-50 border-2 border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-start gap-3"
                >
                  <AlertCircle size={20} className="flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold">{error}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setError('')}
                    className="text-red-400 hover:text-red-600 transition-colors"
                  >
                    <Eye size={16} />
                  </button>
                </motion.div>
              )}
            </AnimatePresence>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {currentContent.email}
              </label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setError(''); // Clear error when user starts typing
                  }}
                  className={`w-full pl-12 pr-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 transition-all ${
                    error 
                      ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                      : 'border-gray-200 focus:ring-green-500 focus:border-transparent'
                  }`}
                  placeholder={currentContent.email}
                  required
                  disabled={loading}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {currentContent.password}
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setError(''); // Clear error when user starts typing
                  }}
                  className={`w-full pl-12 pr-12 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 transition-all ${
                    error 
                      ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                      : 'border-gray-200 focus:ring-green-500 focus:border-transparent'
                  }`}
                  placeholder="••••••••"
                  required
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  disabled={loading}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700 text-white py-3 rounded-xl font-semibold shadow-lg transform transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              {loading ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>{currentContent.logging}</span>
                </div>
              ) : (
                currentContent.login
              )}
            </Button>
          </form>
        </motion.div>

        {/* Footer */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="text-center text-sm text-gray-600 mt-6"
        >
          {currentContent.footer}
        </motion.p>
      </motion.div>
    </div>
  );
};

export default AdminLogin;